// src/sim/weapon-behaviors.ts -- pure, no DOM
// Behavior dispatch system for all weapon types.

import { WEAPONS_BY_ID, type WeaponDef, type WeaponBehavior } from './weapons';
import type { GameState } from './state';
import type { Projectile } from './physics';
import { calcDamage } from './damage';
import { applyShieldedDamage } from './shields';
import { createNapalmEffect, createLiquidDirtEffect } from './active-effects';

export type DetonationHandler = (
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
  vx: number,
  vy: number,
) => void;

/** Shared blast damage logic used by all explosive handlers */
export function applyBlastDamage(
  state: GameState,
  x: number,
  y: number,
  blastRadius: number,
  maxDamage: number,
): void {
  for (let i = 0; i < state.tanks.length; i++) {
    const tank = state.tanks[i];
    if (!tank.alive) continue;
    const dist = Math.hypot(tank.x - x, (tank.y - 1) - y);
    const dmg = calcDamage(dist, blastRadius, maxDamage);
    if (dmg > 0) {
      applyShieldedDamage(tank, dmg, false);
      if (!tank.alive && !state.turn.deathQueue.includes(i)) {
        state.turn.deathQueue.push(i);
        state.turn.killLog.push({
          killer: state.turn.currentPlayer,
          victim: tank.id,
        });
      }
    }
  }
}

/** Standard detonation: carve terrain + apply damage with distance falloff */
export function detonateStandard(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  state.terrain.carve(x, y, weapon.blastRadius);
  applyBlastDamage(state, x, y, weapon.blastRadius, weapon.maxDamage);
  state.collapsing = true;
  state.lastHit = { x, y, radius: weapon.blastRadius, weaponId: weapon.id };
}

/**
 * Roller: on terrain/tank hit, transition to rolling mode instead of detonating.
 * The actual rolling happens in stepRoller (called from stepSim).
 * This handler is called if a roller somehow needs to detonate (fallback).
 */
export function startRoller(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  // If called directly as detonation, just explode normally
  detonateStandard(state, weapon, x, y);
}

/**
 * Step a rolling projectile along terrain surface.
 * Called each tick from stepSim when p.mode === 'rolling'.
 * Returns true if roller should be released (detonated or fell off).
 */
export function stepRoller(
  state: GameState,
  p: Projectile,
  dt: number,
): boolean {
  const weapon = WEAPONS_BY_ID[p.weaponId];
  if (!weapon) return true;

  // Move horizontally
  p.x += p.vx * dt;

  // Clamp to world bounds
  if (p.x < 0 || p.x >= state.world.width) {
    detonateStandard(state, weapon, p.x, p.y);
    return true;
  }

  // Follow terrain surface
  const surfY = state.terrain.surfaceY(Math.round(p.x));
  p.y = surfY - 1;

  // Check tank hits during rolling
  for (let t = 0; t < state.tanks.length; t++) {
    const tank = state.tanks[t];
    if (!tank.alive) continue;
    if (Math.abs(tank.x - p.x) < 10 && Math.abs(tank.y - p.y) < 10) {
      detonateStandard(state, weapon, p.x, p.y);
      return true;
    }
  }

  // Check slope for local minimum (valley detection)
  const leftY = state.terrain.surfaceY(Math.round(p.x) - 3);
  const rightY = state.terrain.surfaceY(Math.round(p.x) + 3);
  const goingRight = p.vx > 0;

  // If terrain ahead is higher (uphill), reverse or detonate
  if (goingRight && rightY < surfY - 2) {
    p.vx = -p.vx; // reverse
  } else if (!goingRight && leftY < surfY - 2) {
    p.vx = -p.vx; // reverse
  }

  // Valley detection: both sides are higher
  if (leftY < surfY - 1 && rightY < surfY - 1) {
    detonateStandard(state, weapon, p.x, p.y);
    return true;
  }

  // Track distance
  p.tunnelTraveled += Math.abs(p.vx * dt);
  if (p.tunnelTraveled > 300) {
    detonateStandard(state, weapon, p.x, p.y);
    return true;
  }

  return false;
}

/**
 * MIRV: splits at apex. The split logic lives in stepSim flying phase
 * because it happens during flight, not on detonation.
 * If a MIRV hits terrain before apex, treat as standard.
 */
export function detonateMirv(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  detonateStandard(state, weapon, x, y);
}

/**
 * Check if a MIRV projectile has reached apex and should split.
 * Returns true if split occurred.
 */
export function checkMirvApex(state: GameState, p: Projectile): boolean {
  const def = WEAPONS_BY_ID[p.weaponId];
  if (!def || def.behavior !== 'mirv' || p.isSub) return false;

  // Apex: vy was negative (going up), now >= 0 (starting to fall)
  if (p.prevVy < 0 && p.vy >= 0) {
    const subCount = def.subCount ?? 5;
    const subWeaponId = def.subWeaponId ?? 'missile';
    for (let i = 0; i < subCount; i++) {
      const sub = state.projectiles.acquire();
      if (!sub) break;
      sub.x = p.x;
      sub.y = p.y;
      sub.vx = p.vx + (i - (subCount - 1) / 2) * 15;
      sub.vy = Math.abs(p.vy) * 0.3 + 10; // slight downward
      sub.weaponId = subWeaponId;
      sub.owner = p.owner;
      sub.isSub = true;
      sub.mode = 'flying';
      sub.prevVy = sub.vy;
      sub.bouncesLeft = 0;
      sub.tunnelTraveled = 0;
    }
    return true; // caller should release original
  }
  return false;
}

/** Funky bomb: on impact, spray subCount random bomblets */
export function detonateFunky(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  // Small initial explosion
  const miniRadius = Math.max(5, weapon.blastRadius * 0.3);
  state.terrain.carve(x, y, miniRadius);
  applyBlastDamage(state, x, y, miniRadius, weapon.maxDamage * 0.3);
  state.lastHit = { x, y, radius: miniRadius, weaponId: weapon.id };
  state.collapsing = true;

  // Spray bomblets
  const subCount = weapon.subCount ?? 7;
  const subWeaponId = weapon.subWeaponId ?? 'baby-missile';
  for (let i = 0; i < subCount; i++) {
    const sub = state.projectiles.acquire();
    if (!sub) break;
    sub.x = x;
    sub.y = y - 2;
    // Fan pattern: upward with horizontal spread
    const angle = -Math.PI / 2 + (state.rng() - 0.5) * (Math.PI * 2 / 3); // -60 to +60 from vertical
    const speed = 100 + state.rng() * 100;
    sub.vx = Math.cos(angle) * speed;
    sub.vy = Math.sin(angle) * speed;
    sub.weaponId = subWeaponId;
    sub.owner = state.turn.currentPlayer;
    sub.isSub = true;
    sub.mode = 'flying';
    sub.prevVy = sub.vy;
    sub.bouncesLeft = 0;
    sub.tunnelTraveled = 0;
  }
}

/** Dirt weapons: add terrain instead of carving */
export function detonateDirt(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  const radius = weapon.dirtRadius ?? weapon.blastRadius;
  state.terrain.addDirt(x, y, radius);
  // Do NOT carve terrain
  state.collapsing = true; // dirt settles, triggers burial check
  state.lastHit = { x, y, radius: Math.min(radius, 15), weaponId: weapon.id };
}

/** Napalm: spawn flowing fire effects */
export function detonateNapalm(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  // Small initial carve at impact
  state.terrain.carve(x, y, weapon.blastRadius);
  state.collapsing = true;
  state.lastHit = { x, y, radius: weapon.blastRadius, weaponId: weapon.id };

  // Spawn two napalm effects flowing left and right
  const dmgPerTick = weapon.maxDamage;
  const flow = weapon.flowRate ?? 60;
  state.activeEffects.push(
    createNapalmEffect(x, y, -1, flow, dmgPerTick, state.turn.currentPlayer),
    createNapalmEffect(x, y, 1, flow, dmgPerTick, state.turn.currentPlayer),
  );
}

/**
 * Digger: transition to tunneling on hit.
 * The actual tunneling happens in stepDigger.
 * This handler is fallback if called directly.
 */
export function startDigger(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  detonateStandard(state, weapon, x, y);
}

/**
 * Step a tunneling projectile through terrain.
 * Returns true if tunneling is complete (detonate and release).
 */
export function stepDigger(
  state: GameState,
  p: Projectile,
  dt: number,
): boolean {
  const weapon = WEAPONS_BY_ID[p.weaponId];
  if (!weapon) return true;

  // Move in tunnel direction
  p.x += p.vx * dt;
  p.y += p.vy * dt;
  p.tunnelTraveled += Math.hypot(p.vx * dt, p.vy * dt);

  // Carve narrow channel at current position
  state.terrain.carve(p.x, p.y, 3);

  // Check if reached tunnel depth
  if (p.tunnelTraveled >= (weapon.tunnelDepth ?? 100)) {
    detonateStandard(state, weapon, p.x, p.y);
    return true;
  }

  // Check if exited the world
  if (p.x < 0 || p.x >= state.world.width || p.y >= state.world.height) {
    return true;
  }

  return false;
}

/** Liquid dirt: deposit dirt flowing along terrain */
export function detonateLiquidDirt(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
): void {
  state.lastHit = { x, y, radius: 10, weaponId: weapon.id };
  state.collapsing = true;
  const flow = weapon.flowRate ?? 40;
  state.activeEffects.push(
    createLiquidDirtEffect(x, y, -1, flow, state.turn.currentPlayer),
    createLiquidDirtEffect(x, y, 1, flow, state.turn.currentPlayer),
  );
}

/** Leapfrog: explode on impact, then re-launch with reduced velocity */
export function detonateLeapfrog(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
  _vx: number,
  _vy: number,
): void {
  // Standard explosion at impact
  detonateStandard(state, weapon, x, y);

  // Check bouncesLeft on the projectile - we pass it via a lookup
  // The calling code should set bouncesLeft before calling
  // We re-launch if there are bounces remaining
  // Note: bouncesLeft is tracked externally via the projectile
}

/**
 * Handle leapfrog re-launch after detonation.
 * Called from the flying phase when a leapfrog hits.
 * Returns true if a new projectile was spawned.
 */
export function handleLeapfrogBounce(
  state: GameState,
  weaponId: string,
  x: number,
  y: number,
  vx: number,
  bouncesLeft: number,
  owner: number,
): boolean {
  if (bouncesLeft <= 0) return false;
  const sub = state.projectiles.acquire();
  if (!sub) return false;
  sub.x = x;
  sub.y = y - 5;
  sub.vx = vx * 0.6;
  sub.vy = -150;
  sub.weaponId = weaponId;
  sub.owner = owner;
  sub.isSub = false;
  sub.mode = 'flying';
  sub.prevVy = -150;
  sub.bouncesLeft = bouncesLeft - 1;
  sub.tunnelTraveled = 0;
  return true;
}

/** Tracer: no damage, no explosion, just disappears */
export function detonateTracer(
  _state: GameState,
  _weapon: WeaponDef,
): void {
  // Do nothing - no crater, no damage, no particles
}

/** Riot weapons: carve wedge-shaped terrain */
export function detonateRiot(
  state: GameState,
  weapon: WeaponDef,
  x: number,
  y: number,
  vx: number,
  vy: number,
): void {
  // Determine direction from velocity or default rightward
  const angle = Math.atan2(vy, vx);
  const length = weapon.blastRadius * 3;
  const startWidth = 2;
  const endWidth = weapon.blastRadius;

  // Carve a wedge/cone shape
  const steps = Math.ceil(length / 2);
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const cx = x + Math.cos(angle) * (t * length);
    const cy = y + Math.sin(angle) * (t * length);
    const r = startWidth + (endWidth - startWidth) * t;
    state.terrain.carve(cx, cy, r);
  }

  // Apply minimal damage to tanks in the wedge area
  if (weapon.maxDamage > 0) {
    applyBlastDamage(state, x, y, length, weapon.maxDamage);
  }

  state.collapsing = true;
  state.lastHit = { x, y, radius: weapon.blastRadius, weaponId: weapon.id };
}

/**
 * Fire a laser hitscan beam from a tank.
 * Traces a ray and hits the first terrain or tank pixel.
 */
export function fireLaser(
  state: GameState,
  tankIdx: number,
  angle: number,
): void {
  const weapon = WEAPONS_BY_ID['laser'];
  if (!weapon) return;
  const tank = state.tanks[tankIdx];
  if (!tank.alive) return;

  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  let x = tank.x;
  let y = tank.y - 4;

  // Trace ray pixel by pixel
  for (let step = 0; step < 960; step++) {
    x += dx;
    y += dy;

    if (x < 0 || x >= state.world.width || y < 0 || y >= state.world.height) break;

    // Check tank hit
    for (let t = 0; t < state.tanks.length; t++) {
      if (t === tankIdx) continue;
      const target = state.tanks[t];
      if (!target.alive) continue;
      if (Math.abs(target.x - x) < 8 && Math.abs(target.y - y) < 6) {
        // Hit tank - carve and damage
        state.terrain.carve(x, y, weapon.blastRadius);
        applyBlastDamage(state, x, y, weapon.blastRadius, weapon.maxDamage);
        state.collapsing = true;
        state.lastHit = { x, y, radius: weapon.blastRadius, weaponId: 'laser' };
        return;
      }
    }

    // Check terrain hit
    if (state.terrain.isSolid(Math.round(x), Math.round(y))) {
      state.terrain.carve(x, y, weapon.blastRadius);
      applyBlastDamage(state, x, y, weapon.blastRadius, weapon.maxDamage);
      state.collapsing = true;
      state.lastHit = { x, y, radius: weapon.blastRadius, weaponId: 'laser' };
      return;
    }
  }
}

// --- All 11 behavior handlers are real implementations ---
export const BEHAVIOR_HANDLERS: Record<WeaponBehavior, DetonationHandler> = {
  standard: detonateStandard,
  roller: startRoller,
  dirt: detonateDirt,
  mirv: detonateMirv,
  funky: detonateFunky,
  napalm: detonateNapalm,
  digger: startDigger,
  leapfrog: detonateLeapfrog,
  tracer: detonateTracer,
  riot: detonateRiot,
  'liquid-dirt': detonateLiquidDirt,
};

/** Main entry point: detonate a weapon by id at position */
export function detonateWeapon(
  state: GameState,
  weaponId: string,
  x: number,
  y: number,
  vx: number,
  vy: number,
): void {
  const def = WEAPONS_BY_ID[weaponId];
  if (!def) {
    detonateStandard(state, { blastRadius: 15, maxDamage: 15 } as WeaponDef, x, y);
    return;
  }
  BEHAVIOR_HANDLERS[def.behavior](state, def, x, y, vx, vy);
}
